import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Link2, Loader2 } from "lucide-react";
import { LOGO_WHITE } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConnectJobber() {
  const [connected, setConnected] = useState(null);
  const [params] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/jobber/status`);
      setConnected(data.connected);
    } catch {
      setConnected(false);
    }
  };

  useEffect(() => {
    load();
    if (params.get("jobber") === "connected") toast.success("Jobber connected! Leads will now flow in automatically.");
    if (params.get("jobber") === "error") toast.error("Jobber connection failed. Please try again.");
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen bg-navy noise-overlay flex flex-col">
      <header className="relative z-10 mx-auto w-full max-w-[900px] px-5 md:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 text-ice hover:text-gold transition-colors">
          <ArrowLeft size={18} />
          <img src={LOGO_WHITE} alt="The Mailbox Brothers LLC" className="h-10 w-auto" />
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex items-center">
        <div className="mx-auto w-full max-w-[640px] px-5 md:px-10 py-16">
          <p className="font-primary uppercase tracking-[0.3em] text-gold text-xs md:text-sm mb-4">Integrations</p>
          <h1 className="font-primary font-bold uppercase tracking-tighter text-ice text-4xl md:text-6xl leading-[0.9] mb-6">
            Jobber<br />Connection
          </h1>
          <p className="font-body text-ice/75 text-lg leading-relaxed mb-10">
            Connect your Jobber account once. After that, every quote submitted on your website
            automatically creates a Client and a Request in Jobber — photos included.
          </p>

          <div className="bg-ice p-8 md:p-10">
            {connected === null ? (
              <div className="flex items-center gap-3 text-navy/70 font-body">
                <Loader2 className="animate-spin" size={20} /> Checking connection…
              </div>
            ) : connected ? (
              <div data-testid="jobber-connected" className="flex flex-col items-start gap-4">
                <div className="flex items-center gap-3 text-navy">
                  <CheckCircle2 className="text-green-600" size={28} />
                  <span className="font-primary uppercase tracking-tight text-2xl">Connected</span>
                </div>
                <p className="font-body text-navy/70">Your website is linked to Jobber. New quotes will appear as Requests in your Jobber account.</p>
                <a
                  href={`${API}/jobber/oauth/start`}
                  data-testid="jobber-reconnect-btn"
                  className="font-primary uppercase text-sm tracking-wide border border-navy/30 text-navy px-6 py-3 hover:bg-navy hover:text-ice transition-colors"
                >
                  Reconnect
                </a>
              </div>
            ) : (
              <div className="flex flex-col items-start gap-5">
                <p className="font-body text-navy/70">Not connected yet. Click below and approve access in Jobber.</p>
                <a
                  href={`${API}/jobber/oauth/start`}
                  data-testid="jobber-connect-btn"
                  className="font-primary uppercase text-base tracking-wide bg-gold text-navy px-8 py-4 flex items-center gap-2 hover:shadow-[5px_5px_0px_#0B2545] transition-shadow"
                >
                  <Link2 size={18} /> Connect Jobber
                </a>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
